import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import MoleculeImage from './MoleculeImage';
import StructureEditor from './StructureEditor';
import API_BASE from '../config';
import { authHeaders, clearEditPassword } from '../services/auth';
import './NewReaction.css';

const API_BASE_URL = API_BASE;

const NewReaction = () => {
  const navigate = useNavigate();
  const [reactionType, setReactionType] = useState('');
  const [reactionTypes, setReactionTypes] = useState([]);
  const [reagentTable, setReagentTable] = useState('');
  const [url, setUrl] = useState('');
  const [experimentalProcedure, setExperimentalProcedure] = useState('');
  const [molecules, setMolecules] = useState([]);
  const [showEditor, setShowEditor] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  useEffect(() => {
    fetch(`${API_BASE_URL}/reaction-types`)
      .then(r => r.json())
      .then(data => setReactionTypes(data.filter(t => t.reaction_type)))
      .catch(() => {});
  }, []);

  const handleMolDrawn = (smiles, molblock) => {
    setMolecules(prev => [...prev, { smiles, molblock, role: 'reactant' }]);
    setShowEditor(false);
  };

  const handleRoleChange = (index, role) => {
    setMolecules(prev => prev.map((m, i) => i === index ? { ...m, role } : m));
  };

  const removeMolecule = (index) => {
    setMolecules(prev => prev.filter((_, i) => i !== index));
  };

  const handleCreate = async () => {
    if (!reactionType.trim()) {
      setSubmitError('Reaction type is required.');
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      // Create the reaction
      const resp = await fetch(`${API_BASE_URL}/reactions`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          reaction_type: reactionType.trim(),
          reagent_table: reagentTable.trim() || undefined,
          url: url.trim() || undefined,
          experimental_procedure: experimentalProcedure.trim() || undefined,
        }),
      });
      if (resp.status === 401) { clearEditPassword(); throw new Error('Wrong password'); }
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || 'Create failed');

      const { id, record_number } = data;

      // Add molecules
      for (const mol of molecules) {
        const mResp = await fetch(`${API_BASE_URL}/reactions/${id}/molecules`, {
          method: 'POST',
          headers: authHeaders(),
          body: JSON.stringify({ smiles: mol.smiles, mol_block: mol.molblock, role: mol.role }),
        });
        const mData = await mResp.json();
        if (!mResp.ok) throw new Error(mData.error || 'Failed to add molecule');
      }

      navigate(`/reaction/${record_number}`);
    } catch (err) {
      setSubmitError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="new-reaction-container">
      <div className="new-reaction-header">
        <Link to="/" className="back-link">← Back to list</Link>
        <h1>New Reaction</h1>
      </div>

      <div className="new-reaction-form">
        <div className="form-field">
          <label>Reaction Type <span className="required">*</span></label>
          <select
            value={reactionType}
            onChange={e => setReactionType(e.target.value)}
            className="form-select"
          >
            <option value="">— Select reaction type —</option>
            {reactionTypes.map(t => (
              <option key={t.reaction_type} value={t.reaction_type}>
                {t.reaction_type}{t.count > 0 ? ` (${t.count})` : ''}
              </option>
            ))}
          </select>
        </div>

        <div className="form-field">
          <label>Reagents / Conditions</label>
          <textarea
            value={reagentTable}
            onChange={e => setReagentTable(e.target.value)}
            placeholder="Reagents, solvents, temperature, yield…"
            rows={5}
            className="form-textarea"
          />
        </div>

        <div className="form-field">
          <label>Experimental Procedure</label>
          <textarea
            value={experimentalProcedure}
            onChange={e => setExperimentalProcedure(e.target.value)}
            placeholder="Describe the experimental procedure…"
            rows={5}
            className="form-textarea"
          />
        </div>

        <div className="form-field">
          <label>Reference URL</label>
          <input
            type="text"
            value={url}
            onChange={e => setUrl(e.target.value)}
            placeholder="https://…"
            className="form-input"
          />
        </div>
      </div>

      <div className="new-reaction-molecules">
        <div className="molecules-section-header">
          <h2>Molecules</h2>
          <button className="add-mol-btn" onClick={() => setShowEditor(true)}>
            + Add Molecule
          </button>
        </div>

        {molecules.length === 0 && (
          <p className="no-molecules-hint">No molecules added yet. Click "+ Add Molecule" to draw one.</p>
        )}

        <div className="new-mol-cards">
          {molecules.map((mol, i) => (
            <div key={i} className="new-mol-card">
              <MoleculeImage smiles={mol.smiles} width={160} height={160} />
              <div className="new-mol-card-controls">
                <select
                  className="role-select"
                  value={mol.role}
                  onChange={e => handleRoleChange(i, e.target.value)}
                >
                  <option value="reactant">reactant</option>
                  <option value="reagent">reagent</option>
                  <option value="product">product</option>
                </select>
                <button className="remove-mol-btn" onClick={() => removeMolecule(i)}>
                  Remove
                </button>
              </div>
              <div className="new-mol-smiles">{mol.smiles}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="new-reaction-actions">
        <button className="create-btn" onClick={handleCreate} disabled={submitting}>
          {submitting ? 'Creating…' : 'Create Reaction'}
        </button>
        <Link to="/" className="cancel-link">Cancel</Link>
        {submitError && <span className="submit-error">{submitError}</span>}
      </div>

      {showEditor && (
        <StructureEditor
          onSave={handleMolDrawn}
          onClose={() => setShowEditor(false)}
        />
      )}
    </div>
  );
};

export default NewReaction;
